import * as React from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import styled from 'styled-components';

import { translateRoleToValue } from 'services/TransformService';
import { Colors } from 'services/ColorService';
import { useIsErpAdmin } from 'core/hooks';

import type { ErpUserNode } from 'core/state/userState';

import {
  UsersSettingsTableComponent,
  IColumn,
  IRow,
} from './UsersSettingsTable';

export interface IUsersSettingsTable {
  data: ErpUserNode[];
  loading?: boolean;
  error?: string;
  editUser: (userId: string) => void;
  loadMore: () => void;
  hasMore: boolean;
}

const getColumnsSource = (t: TFunction, isAdmin: boolean): IColumn[] => [
  {
    Header: `${t('table.userSettings.name')}`,
    accessor: 'username',
    columnWidth: '240px',
  },
  {
    Header: `${t('table.userSettings.email')}`,
    accessor: 'email',
    columnWidth: '264px',
    marginRight: isAdmin ? '32px' : '72px',
    marginLeft: isAdmin ? '32px' : '72px',
  },
  // ТЗ 0083: рабочую почту чужого сотрудника сервер отдаёт только Admin —
  // у остальных колонка была бы сплошным «не привязана».
  ...(isAdmin
    ? [
        {
          Header: `${t('staffMailbox.column')}`,
          accessor: 'workspace' as const,
          columnWidth: '264px',
          marginRight: '32px',
        },
      ]
    : []),
  {
    Header: `${t('table.userSettings.role')}`,
    accessor: 'role',
    flexible: true,
    marginRight: '120px',
  },
];

const transformUsers = (users: ErpUserNode[], t: TFunction) =>
  users.map((user) => {
    const box = user.workspaceMailbox;
    return {
      id: user.id,
      username: `${user.lastName} ${user.firstName}${
        user.middleName ? ` ${user.middleName}` : ''
      }`,
      email: user?.email || '',
      role: user.roles.map((role) => translateRoleToValue(role)).join(', '),
      workspace: box?.email ? (
        <WorkspaceEmail title={`${t('staffMailbox.linkedTitle')}`}>
          {box.email}
        </WorkspaceEmail>
      ) : (
        <WorkspaceMissing>{t('staffMailbox.notLinked')}</WorkspaceMissing>
      ),
    };
  });

export const UsersSettingsTable = memo(
  ({
    data,
    loading,
    error,
    editUser,
    loadMore,
    hasMore,
  }: IUsersSettingsTable) => {
    const { t } = useTranslation();
    const isAdmin = useIsErpAdmin();
    const columns: IColumn[] = useMemo(() => getColumnsSource(t, isAdmin), [
      t,
      isAdmin,
    ]);
    const rows: IRow[] = useMemo(() => transformUsers(data, t), [data, t]);

    if (error) {
      return <p>{error}</p>;
    }

    return (
      <UsersSettingsTableComponent
        rows={rows}
        columns={columns}
        isFlexible
        onUserEditClick={editUser}
        noDataText={t('table.userSettings.noData')}
        loadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
      />
    );
  },
);

const WorkspaceEmail = styled.span`
  color: ${Colors.Black};
  word-break: break-all;
`;

const WorkspaceMissing = styled.span`
  color: ${Colors.Gray};
  font-weight: 400;
`;
